import { useInfiniteQuery, useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import shopService from "services/shop";
import categoryService from "services/category";
import bannerService from "services/banner";
import useUserLocation from "hooks/useUserLocation";
import qs from "qs";
import { useRouter } from "next/router";
import cls from "./homev1.module.scss";
import { useAuth } from "contexts/auth/auth.context";
import BannerContainer from "containers/banner/banner";
import Actions from "components/home-actions/home-actions";
import ShopList from "containers/shopList/shopList";
import ShopCategoryList from "containers/shopCategoryList/v1";
import Grocery from "components/grocery/grocery";
import InviteFriend from "components/invite-friend/invite-friend";
import BecomeSellerBanner from "components/becomeSellerBanner/becomeSellerBanner";
import { useMediaQuery } from "@mui/material";
import dynamic from "next/dynamic";
import useModal from "hooks/useModal";
import { META_TITLE } from "constants/config";
import { useSettings } from "contexts/settings/settings.context";
import recipeService from "services/recipe";
import RecipeListSwiper from "containers/recipeList/recipeListSwiper";
import FEATURES from "constants/features"; // ✅ flag ricette

const MobileSearchContainer = dynamic(
  () => import("containers/mobileSearchContainer/mobileSearchContainer"),
);
const VisitedShops = dynamic(
  () => import("components/visitedShops/visitedShops"),
);

export default function Home() {
  const { t, i18n } = useTranslation();
  const { query } = useRouter();
  const isDesktop = useMediaQuery("(min-width: 1140px)");
  const locale = i18n.language;
  const { isAuthenticated } = useAuth();
  const { settings } = useSettings();
  const location = useUserLocation();
  const [searchModal, handleOpenSearchModal, handleCloseSearchModal] =
    useModal();

  const { data: shopCategoryList, isLoading: shopCategoryLoading } = useQuery(
    ["shopCategory", locale],
    () => categoryService.getAllShopCategories({ perPage: 20 }),
  );

  const { data: banners, isLoading: isBannerLoading } = useQuery(
    ["banners", locale],
    () => bannerService.getAll(),
  );

  const shopParams = {
    category_id: query?.category_id || undefined,
    address: location,
    open: query?.open || undefined,
    column: query?.column || undefined,
    sort: query?.sort || undefined,
    work_24_7: query?.work_24_7 || undefined,
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery(
      [
        "restaurants",
        locale,
        location,
        query?.column,
        query?.sort,
        query?.open,
        query?.work_24_7,
        query?.category_id,
      ],
      ({ pageParam = 1 }) =>
        shopService.getAll(
          qs.stringify({
            page: pageParam,
            perPage: 8,
            ...shopParams,
          }),
        ),
      {
        getNextPageParam: (lastPage: any) => {
          if (lastPage.meta.current_page < lastPage.meta.last_page) {
            return lastPage.meta.current_page + 1;
          }
          return undefined;
        },
      },
    );

  const { data: visitedShops } = useQuery({
    queryKey: ["visitedShops", locale, isAuthenticated],
    queryFn: () =>
      shopService.getVisitedShops({
        perPage: 10,
        page: 1,
      }),
    staleTime: 0,
  });

  const { data: recipes, isLoading: isRecipesLoading } = useQuery({
    queryKey: ["recipes", locale, shopParams?.category_id],
    queryFn: () =>
      recipeService.getAll({
        perPage: 10,
        category_id: shopParams?.category_id,
      }),
    enabled: FEATURES.recipes, // ✅ niente chiamata API se il modulo è disattivo
  });

  const restaurants = data?.pages?.flatMap((item) => item.data) || [];

  return (
    <>
      <section className={cls.searchContainer}>
        <div className="container">
          <button
            type="button"
            className={cls.search}
            onClick={handleOpenSearchModal}
          >
            <span>{`${t("search.on")} Gloco`}</span>
          </button>
        </div>
      </section>
      <section className="container">
        <div className={cls.bannerContainer}>
          <BannerContainer
            banners={banners?.data || []}
            loadingBanner={isBannerLoading}
          />
        </div>
      </section>
      <section className="container">
        <div className={cls.homeActionContainer}>
          <Actions />
        </div>
      </section>
      <section className="container">
        <div className={cls.shopCategoryListContainer}>
          <ShopCategoryList
            data={
              shopCategoryList?.data?.sort((a, b) => a?.input - b?.input) || []
            }
            loading={shopCategoryLoading}
          />
        </div>
      </section>
      {!!visitedShops?.data?.length && (
        <section className="container">
          <div>
            <VisitedShops data={visitedShops?.data} />
          </div>
        </section>
      )}
      <section className="container">
        <div className={cls.shopListContainer}>
          <ShopList
            title={t("all.shops")}
            shops={restaurants}
            loading={isLoading && !isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            fetchingNextPage={isFetchingNextPage}
          />
        </div>
      </section>

      {/* ✅ Blocco ricette mostrato solo se il flag è attivo */}
      {FEATURES.recipes && (
        <section className="container">
          <div className={cls.recipeListContainer}>
            <RecipeListSwiper
              data={recipes?.data}
              loading={isRecipesLoading}
              title={t("recipes")}
            />
          </div>
        </section>
      )}

      <section className="container">
        <div className={cls.groceryContainer}>
          <Grocery />
        </div>
      </section>
      <section className={cls.inviteFriendContainer}>
        <InviteFriend />
      </section>
      <section className="container">
        <div className={cls.becomeSellerContainer}>
          <BecomeSellerBanner />
        </div>
      </section>
      <MobileSearchContainer
        open={searchModal}
        onClose={handleCloseSearchModal}
        fullScreen
      />
    </>
  );
}
